
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import List from '@/common/components/UI/List/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'

// module components
import ScopesListItem from '@/modules/GatewayModule/components/ScopesListItem/index.vue'
import ScopesActions from '@/modules/GatewayModule/components/ScopesActions/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { GatewayScopeT } from '@/core/types/gateway.types'

// realisations
import { gateway } from '@/core/realisations/gateway.realisation'

// composable
import useActions from '@/common/composable/useActions'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        list: List,
        'failed-fetch': FailedFetch,
        'the-button': TheButton,
        'scopes-list-item': ScopesListItem,
        'scopes-actions': ScopesActions,
        'the-select': TheSelect,
        'the-input': TheInput,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            scopes: new DataState<GatewayScopeT>({}),
        })

        const filters_state = reactive({
            scope: null as string | null,
            is_active: null as string | null,
        })

        const active_options = [
            { name: 'Активен', value: '1' },
            { name: 'Не активен', value: '0' },
        ]

        const loadScopes = async (page: number) => {
            state.scopes.pagination.current_page = page

            try {
                state.scopes.is_loading = true
                const params = {
                    scope: filters_state.scope || undefined,
                    isActive: filters_state.is_active ? Number(filters_state.is_active) : undefined,
                }

                const response = await gateway.getScopes(page, params)
                state.scopes.list = response.data
                state.scopes.pagination.total_pages = response.meta.last_page

                state.scopes.is_failed = false
                state.scopes.failure.error_state.is_cancel = false
            } catch (error) {
                state.scopes.failure.catchError(error)

                if (!state.scopes.failure.error_state.is_cancel) {
                    state.scopes.is_failed = true
                }
            } finally {
                if (!state.scopes.failure.error_state.is_cancel) {
                    state.scopes.is_loading = false
                }
            }
        }

        onBeforeMount(() => {
            loadScopes(1)
        })

        const { actions_state, startCreate, startUpdate } = useActions<GatewayScopeT>()

        const createScope = async ({
            scope,
            description,
            isActive,
        }: {
            scope: string
            description: string
            isActive: boolean
        }) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.createScope(scope, description, isActive)
                await loadScopes(state.scopes.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const updateScope = async ({
            id,
            scope,
            description,
            isActive,
        }: {
            id: number
            scope: string
            description: string
            isActive: boolean
        }) => {
            try {
                actions_state.is_actions_loading = true
                await gateway.updateScope(id, scope, description, isActive)
                await loadScopes(state.scopes.pagination.current_page)
                actions_state.is_actions_opened = false
            } catch (error) {
                console.error(error)
            } finally {
                actions_state.is_actions_loading = false
            }
        }

        const resetFilters = () => {
            filters_state.scope = null
            filters_state.is_active = null

            loadScopes(1)
        }

        const searchScope = async () => {
            state.is_loading = true

            await loadScopes(1)

            state.is_loading = false
        }

        return {
            state,
            filters_state,
            loadScopes,
            actions_state,
            startCreate,
            startUpdate,
            createScope,
            updateScope,
            resetFilters,
            searchScope,
            active_options,
        }
    },
})
